import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["interpello", "ruling", "richiesta", "risposta", "verifica", "questionario", "scambio_info", "altro"];

export default function AdeCommunications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [openInterpello, setOpenInterpello] = useState(false);
  const [form, setForm] = useState({ type: "interpello", subject: "", direction: "inviata", communication_date: new Date().toISOString().slice(0, 10), summary: "", status: "bozza" });
  const [interpelloDesc, setInterpelloDesc] = useState("");
  const [interpelloResult, setInterpelloResult] = useState(null);
  const [interpelloLoading, setInterpelloLoading] = useState(false);

  const load = async () => { const { data } = await api.get("/ade-communications"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.subject) return toast.error("Oggetto richiesto");
    try {
      await api.post("/ade-communications", form);
      toast.success("Comunicazione registrata");
      setOpen(false); load();
    } catch (e) { toast.error("Errore"); }
  };

  const analyze = async () => {
    if (!interpelloDesc) return;
    setInterpelloLoading(true);
    try {
      const { data } = await api.post("/ai/analyze-interpello", { description: interpelloDesc });
      setInterpelloResult(data);
    } catch (e) { toast.error("Errore AI"); }
    finally { setInterpelloLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="ade-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Dialogo Cooperative Compliance</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Comunicazioni ADE</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={openInterpello} onOpenChange={setOpenInterpello}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-sm" data-testid="new-interpello-btn"><Sparkles size={14} className="mr-1" /> Interpello assistito</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Interpello assistito AI</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Label className="label-tag">Descrivi l'incertezza interpretativa</Label>
                <Textarea rows={6} value={interpelloDesc} onChange={(e) => setInterpelloDesc(e.target.value)} className="rounded-sm" data-testid="interpello-desc" />
                <Button onClick={analyze} disabled={interpelloLoading} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="interpello-analyze">
                  {interpelloLoading ? "Analisi…" : "Analizza con AI"}
                </Button>
                {interpelloResult?.parsed && (
                  <Card className="card-flat p-4 mt-3 text-sm space-y-2">
                    <div><b>Tipologia:</b> {interpelloResult.parsed.interpello_type}</div>
                    <div><b>Norme:</b> {(interpelloResult.parsed.relevant_norms || []).join(", ")}</div>
                    <div><b>Struttura:</b> <div className="whitespace-pre-wrap text-slate-700">{interpelloResult.parsed.recommended_structure}</div></div>
                    {interpelloResult.parsed.draft && <div><b>Bozza:</b> <div className="whitespace-pre-wrap text-slate-700 mt-1">{interpelloResult.parsed.draft}</div></div>}
                  </Card>
                )}
                {interpelloResult && !interpelloResult.parsed && (
                  <Card className="card-flat p-4 mt-3 text-sm whitespace-pre-wrap">{interpelloResult.raw}</Card>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="new-ade-btn"><Plus size={14} className="mr-1" /> Nuova comunicazione</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuova comunicazione ADE</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Oggetto" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className="rounded-sm" />
                <Select value={form.direction} onValueChange={(v) => setForm((f) => ({ ...f, direction: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="inviata">Inviata</SelectItem><SelectItem value="ricevuta">Ricevuta</SelectItem></SelectContent>
                </Select>
                <Input type="date" value={form.communication_date} onChange={(e) => setForm((f) => ({ ...f, communication_date: e.target.value }))} className="rounded-sm" />
                <Textarea placeholder="Riassunto" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} className="rounded-sm" />
              </div>
              <DialogFooter><Button onClick={create} className="rounded-sm bg-slate-900 hover:bg-slate-800">Salva</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase tracking-wider text-slate-600"><tr><th className="py-3 px-3 text-left">Tipo</th><th className="py-3 px-3 text-left">Oggetto</th><th className="py-3 px-3 text-left">Direz.</th><th className="py-3 px-3 text-left">Data</th><th className="py-3 px-3 text-left">Stato</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">Nessuna comunicazione</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className="border-b border-slate-100">
                <td className="py-3 px-3"><Badge variant="outline" className="rounded-sm">{i.type}</Badge></td>
                <td className="py-3 px-3 text-slate-800">{i.subject}</td>
                <td className="py-3 px-3 text-slate-600">{i.direction}</td>
                <td className="py-3 px-3 text-xs data-num text-slate-500">{i.communication_date}</td>
                <td className="py-3 px-3"><Badge variant="outline" className="rounded-sm">{i.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
